import React, { useEffect } from 'react';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import styles from './LoginForm.module.scss';
import { useTranslation } from 'react-i18next';
import Input from '../../../../../Common/Input/Input';
import InputPassword from '../../../../../Common/Input/InputPassword';
import ButtonPrimary from '../../../../../Common/Button/Primary/ButtonPrimary';
import { useAppDispatch, useAppSelector } from '../../../../../../config/redux/hooks';
import { login, resetLogin } from '../../loginSlice';
import { useNavigate } from 'react-router-dom';
import ForgotPasswordLink from '../ForgotPasswordLink/ForgotPasswordLink';
import useNotifyOnError from '../../../../../../common/form/useNotifyOnError';

interface Props {
  withForgotPassword?: boolean;
}

function LoginForm({ withForgotPassword }: Props) {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const navigation = useNavigate();
  const error = useAppSelector(state => state.auth.login.error);
  const redirectToHome = useAppSelector(state => state.auth.login.redirectToHome);

  useNotifyOnError(error);

  useEffect(() => {
    if (redirectToHome) {
      dispatch(resetLogin());
      navigation('/');
    }
  }, [redirectToHome]);

  const formik = useFormik({
    initialValues: {
      email: '',
      password: '',
    },
    validationSchema: Yup.object({
      email: Yup.string().email(t('common.form.validation.email')).required(t('common.form.validation.required')),
      password: Yup.string()
        .required(t('common.form.validation.password.required'))
        .min(6, t('common.form.validation.password.short'))
        .max(256, t('common.form.validation.password.long')),
    }),
    onSubmit: values => {
      dispatch(login(values));
    },
  });

  return (
    <form onSubmit={formik.handleSubmit} className={styles.form}>
      <Input
        label={t('common.form.label.email')}
        id={'email'}
        name={'email'}
        type={'email'}
        placeholder={t('common.form.placeholder.email')}
        onChange={formik.handleChange}
        onBlur={formik.handleBlur}
        value={formik.values.email}
        touched={formik.touched.email}
        error={formik.errors.email}
      />
      <InputPassword
        label={t('common.form.label.password')}
        id={'password'}
        name={'password'}
        type={'password'}
        placeholder={t('common.form.placeholder.password')}
        onChange={formik.handleChange}
        onBlur={formik.handleBlur}
        value={formik.values.password}
        touched={formik.touched.password}
        error={formik.errors.password}
        additionalRowInside={withForgotPassword && <ForgotPasswordLink right />}
      />
      <ButtonPrimary type={'submit'} label={t('login.action.form.submit')} />
    </form>
  );
}

export default LoginForm;
